import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  UsersRound,
  Mail,
  Copy,
  Check,
  Trash2,
  AlertCircle,
} from "lucide-react";

/**
 * MembershipShare — Premier annual perk: invite up to 2 family/friends.
 *
 * Shown inside the customer Membership tab. Hidden when the member isn't
 * on `plan_premier_annual`. Surfaces invite slots used/available, lets the
 * host send invites by email AND copy a shareable link.
 */
export default function MembershipShare() {
  const [state, setState] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [lastInvite, setLastInvite] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = () =>
    api
      .get("/memberships/share/state")
      .then((r) => setState(r.data))
      .catch(() => setState({ eligible: false }));

  useEffect(() => {
    load();
  }, []);

  if (!state) return null;
  if (!state.eligible) return null; // hidden for non-Premier-annual members

  const create = async () => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/memberships/share/invite", {
        email: email || undefined,
        name: name || undefined,
      });
      setLastInvite(data.invite);
      setEmail("");
      setName("");
      setShowForm(false);
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not create invite.");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this invite? If it's already been claimed, that member will lose access.")) {
      return;
    }
    setBusy(true);
    try {
      await api.delete(`/memberships/share/invite/${id}`);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const counts = state.counts || { used: 0, max: 2 };
  const slotsLeft = Math.max(0, counts.max - counts.used);

  return (
    <div className="space-y-6" data-testid="membership-share">
      <div className="rounded-2xl p-6 lg:p-7 border border-[var(--ff-border)] bg-white">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.24em] text-[var(--ff-secondary)]">
              <UsersRound size={14} /> Share Your Membership
            </div>
            <h3 className="mt-2 font-display text-2xl text-[var(--ff-text-primary)]">
              Invite up to 2 family or friends.
            </h3>
            <p className="mt-2 text-sm text-[var(--ff-text-secondary)] leading-relaxed max-w-xl">
              Invite up to 2 family members or friends to enjoy your Premier
              membership access. They'll be able to shop eligible member-only
              drops while your membership is active.
            </p>
          </div>
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
            style={{ background: "rgba(244,162,97,0.18)", color: "#B5651A" }}
            data-testid="share-slots"
          >
            {counts.used} / {counts.max} invites used
          </span>
        </div>

        {/* Invite list */}
        {(state.invites || []).length > 0 && (
          <ul className="mt-6 space-y-2" data-testid="share-invite-list">
            {state.invites.map((inv) => (
              <li
                key={inv.id}
                className="flex items-center justify-between gap-3 p-3 rounded-xl bg-[var(--ff-muted)] border border-[var(--ff-border)]"
                data-testid={`share-invite-${inv.id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-[var(--ff-text-primary)] truncate">
                    {inv.claimed_user?.email ||
                      inv.invite_email ||
                      inv.invite_name ||
                      "Open invite"}
                  </p>
                  <p className="text-xs text-[var(--ff-text-muted)]">
                    {inv.status === "accepted"
                      ? "Linked — access active"
                      : inv.status === "pending"
                      ? `Pending · expires ${new Date(inv.expires_at).toLocaleDateString()}`
                      : inv.status}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => revoke(inv.id)}
                  disabled={busy}
                  className="text-rose-700 hover:text-rose-900 inline-flex items-center gap-1 text-xs"
                  data-testid={`share-revoke-${inv.id}`}
                >
                  <Trash2 size={12} />
                  {inv.status === "accepted" ? "Remove" : "Revoke"}
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Add invite */}
        {slotsLeft > 0 && (
          <div className="mt-6" data-testid="share-add-block">
            {!showForm ? (
              <button
                type="button"
                onClick={() => setShowForm(true)}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="share-invite-btn"
              >
                <Mail size={14} /> Invite Member
              </button>
            ) : (
              <div className="space-y-3 max-w-md" data-testid="share-form">
                <div>
                  <label className="text-xs uppercase tracking-wider text-[var(--ff-text-muted)] block mb-1">
                    Their email (optional — leave blank for shareable link only)
                  </label>
                  <input
                    type="email"
                    className="input-base w-full"
                    placeholder="friend@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="share-email"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[var(--ff-text-muted)] block mb-1">
                    Their name (optional)
                  </label>
                  <input
                    className="input-base w-full"
                    placeholder="Friend's name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="share-name"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={create}
                    disabled={busy}
                    className="btn-primary inline-flex items-center gap-2"
                    data-testid="share-create"
                  >
                    {busy ? "Creating…" : "Create invite"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="btn-secondary"
                    data-testid="share-cancel"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Last invite reveal — shareable link */}
        {lastInvite && (
          <div
            className="mt-5 rounded-xl p-4 bg-[rgba(244,162,97,0.12)] border border-[rgba(244,162,97,0.3)]"
            data-testid="share-last-invite"
          >
            <p className="text-xs font-semibold uppercase tracking-wider text-[#B5651A]">
              Invite created
            </p>
            {lastInvite.invite_email && (
              <p className="mt-1 text-sm text-[var(--ff-text-primary)]">
                Email sent to <strong>{lastInvite.invite_email}</strong>.
              </p>
            )}
            <div className="mt-3">
              <p className="text-[10px] uppercase tracking-wider text-[var(--ff-text-muted)] mb-1">
                Shareable link
              </p>
              <div className="flex gap-2 items-center bg-white rounded-lg p-2 border border-[var(--ff-border)]">
                <code className="flex-1 text-xs text-[var(--ff-text-secondary)] truncate font-mono">
                  {lastInvite.invite_url}
                </code>
                <button
                  type="button"
                  onClick={() => copy(lastInvite.invite_url)}
                  className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-[var(--ff-primary)] hover:text-[var(--ff-primary-hover)]"
                  data-testid="share-copy-link"
                >
                  {copied ? <Check size={12} /> : <Copy size={12} />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
            </div>
          </div>
        )}

        {err && (
          <p className="mt-4 text-sm text-rose-700 inline-flex items-center gap-1.5" data-testid="share-err">
            <AlertCircle size={14} /> {err}
          </p>
        )}
      </div>
    </div>
  );
}
